
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calculator } from "lucide-react";
import { Metal, Unit, GoldPurity, SilverPurity } from "@/lib/pricing";
import { ValuationResult as ValuationResultComponent } from "./ValuationResult";
import { ValuationForm } from "./ValuationForm";
import { ValuationError } from "./ValuationError";
import { ValuationActions } from "./ValuationActions";
import { useValuationCalculation } from "@/hooks/useValuationCalculation";
import { useStore } from "@/store/useStore";
import { DEFAULT_GOLD_PURITY, DEFAULT_SILVER_PURITY } from "@/lib/constants";

interface ValuationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ValuationModal({ isOpen, onClose }: ValuationModalProps) {
  // Initialize with consistent default values
  const [selectedMetal, setSelectedMetal] = useState<Metal>("ORO");
  const [goldPurity, setGoldPurity] = useState<GoldPurity>(DEFAULT_GOLD_PURITY);
  const [silverPurity, setSilverPurity] = useState<SilverPurity>(DEFAULT_SILVER_PURITY);
  const [weight, setWeight] = useState<string>("1");
  const [unit, setUnit] = useState<Unit>("g");
  
  const { currentGoldPrice, currentSilverPrice } = useStore();
  const goldPrice = currentGoldPrice?.price || 0;
  const silverPrice = currentSilverPrice?.price || 0;

  // Use the custom hook for valuation calculations
  const { result, errorState, handleRefreshPrices } = useValuationCalculation({
    selectedMetal,
    goldPurity,
    silverPurity,
    weight,
    unit
  });

  const handleMetalChange = (value: string) => {
    setSelectedMetal(value as Metal);
  };

  const handlePurityChange = (value: string) => {
    console.log("Purity changed to:", value);
    if (selectedMetal === "ORO") {
      setGoldPurity(value as GoldPurity);
    } else {
      setSilverPurity(value as SilverPurity);
    }
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setWeight(value);
    }
  };

  const handleUnitChange = (value: string) => {
    setUnit(value as Unit);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calcola Valore Metalli Preziosi
          </DialogTitle>
          <DialogDescription>
            Calcola il valore di oro e argento in base alla purezza e al peso
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <ValuationForm
            selectedMetal={selectedMetal}
            goldPurity={goldPurity}
            silverPurity={silverPurity}
            weight={weight}
            unit={unit}
            onMetalChange={handleMetalChange}
            onPurityChange={handlePurityChange}
            onWeightChange={handleWeightChange}
            onUnitChange={handleUnitChange}
            goldPrice={goldPrice}
            silverPrice={silverPrice}
            error={errorState}
            onRefreshPrices={handleRefreshPrices}
          />

          {result && !errorState && (
            <ValuationResultComponent 
              result={result}
              selectedMetal={selectedMetal}
              goldPurity={goldPurity}
              silverPurity={silverPurity}
              weight={weight}
              unit={unit}
            />
          )}
          
          {errorState && (
            <ValuationError 
              errorMessage={errorState} 
              onRefresh={handleRefreshPrices} 
            />
          )}
        </div>

        <DialogFooter>
          <ValuationActions
            result={result}
            selectedMetal={selectedMetal}
            goldPurity={goldPurity}
            silverPurity={silverPurity}
            weight={weight}
            unit={unit}
            errorState={errorState}
            onClose={onClose}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
